import { useEffect, useMemo, useState } from "react";
import api from "../../../lib/api";
import { Plus, Trash2, Calendar, Clock, Copy } from "lucide-react";

export default function AvailabilityManager() {
  const [slots, setSlots] = useState([]);
  const [adding, setAdding] = useState({ date: "", times: "10:00, 14:00", duration_minutes: 120, service: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/availability").then(({ data }) => setSlots(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addSlots = async () => {
    if (!adding.date || !adding.times) return;
    setLoading(true);
    try {
      const times = adding.times.split(",").map((t) => t.trim()).filter(Boolean);
      const newSlots = times.map((time) => ({
        date: adding.date,
        time,
        duration_minutes: Number(adding.duration_minutes) || 120,
        service: adding.service || "",
        notes: "",
        booked: false,
      }));
      await api.post("/availability/bulk", newSlots);
      setAdding({ ...adding, times: "10:00, 14:00" });
      load();
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    await api.delete(`/availability/${id}`);
    load();
  };

  // Memoize grouped + sorted data so it doesn't recompute on every render
  const { byDate, dates } = useMemo(() => {
    const grouped = {};
    slots.forEach((s) => {
      if (!grouped[s.date]) grouped[s.date] = [];
      grouped[s.date].push(s);
    });
    Object.keys(grouped).forEach((d) => {
      grouped[d].sort((a, b) => a.time.localeCompare(b.time));
    });
    return { byDate: grouped, dates: Object.keys(grouped).sort() };
  }, [slots]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="availability-manager">
      <div className="mb-8">
        <h1 className="font-serif text-4xl">Disponibilités</h1>
        <p className="text-[#4A3F35] mt-2">Créneaux ouverts à la réservation. Les clients ne peuvent réserver que parmi ceux-ci.</p>
      </div>

      <div className="bg-[#FDFBF7] rounded-2xl p-6 border border-[#2D2A26]/10 mb-6">
        <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C87A5E]" /> Ajouter des créneaux</h3>
        <div className="grid md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-1">
            <label className="overline block mb-1.5">Date</label>
            <input type="date" min={today} value={adding.date} onChange={(e) => setAdding({ ...adding, date: e.target.value })} className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm" data-testid="add-date" />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-1.5">Horaires (séparés par virgule)</label>
            <input value={adding.times} onChange={(e) => setAdding({ ...adding, times: e.target.value })} placeholder="10:00, 14:00, 16:30" className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm" data-testid="add-times" />
          </div>
          <div>
            <label className="overline block mb-1.5">Durée (min)</label>
            <input type="number" value={adding.duration_minutes} onChange={(e) => setAdding({ ...adding, duration_minutes: e.target.value })} className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="overline block mb-1.5">Service (optionnel)</label>
            <select value={adding.service} onChange={(e) => setAdding({ ...adding, service: e.target.value })} className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm">
              <option value="">Tous</option>
              <option value="Familiale">Familiale</option>
              <option value="Couple">Couple</option>
              <option value="Naissance">Naissance</option>
              <option value="Grossesse">Grossesse</option>
            </select>
          </div>
        </div>
        <button onClick={addSlots} disabled={loading || !adding.date} className="btn-primary mt-4 disabled:opacity-50" data-testid="add-slots-btn">
          {loading ? "Ajout…" : "Ajouter les créneaux"}
        </button>
      </div>

      {dates.length === 0 ? (
        <div className="bg-[#FDFBF7] rounded-2xl border border-[#2D2A26]/10 p-12 text-center text-[#4A3F35]/60">
          <Calendar size={36} className="mx-auto mb-3 text-[#4A3F35]/30" />
          Aucun créneau pour le moment. Ajoutez-en ci-dessus.
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((date) => (
            <div key={date} className="bg-[#FDFBF7] rounded-2xl p-5 border border-[#2D2A26]/10" data-testid={`day-${date}`}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={16} className="text-[#C87A5E]" />
                <span className="font-serif text-lg capitalize">{new Date(date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="text-xs text-[#4A3F35]/60 ml-2">{byDate[date].length} créneau{byDate[date].length > 1 ? "x" : ""}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {byDate[date].map((s) => (
                  <div key={s.id} className={`group inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm border ${s.booked ? "bg-[#C87A5E]/10 border-[#C87A5E]/30 text-[#C87A5E]" : "bg-[#F6F3EC] border-transparent text-[#2D2A26]"}`} data-testid={`slot-${s.id}`}>
                    <Clock size={12} />
                    <span className="font-medium">{s.time}</span>
                    <span className="text-xs opacity-60">{s.duration_minutes}min</span>
                    {s.service && <span className="text-xs bg-white/60 px-1.5 py-0.5 rounded">{s.service}</span>}
                    {s.booked && <span className="text-xs uppercase tracking-wider">Réservé</span>}
                    {!s.booked && (
                      <button onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 text-red-700 transition" title="Supprimer">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
